import React from "react";
import { useReviews } from "@/hooks/useReviews";

const SPARK_ICON_URL =
  "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685663/img/construction_spark_icon.png";

interface RatingSummaryProps {
  className?: string;
  variant?: "inline" | "stacked";
}

export function RatingSummary({
  className = "",
  variant = "inline",
}: RatingSummaryProps) {
  const { reviews, loading, error } = useReviews();

  if (loading && (!reviews || reviews.length === 0)) {
    return null;
  }

  if (error || !reviews || reviews.length === 0) {
    return null;
  }

  const totalReviews = reviews.length;
  const totalScore = reviews.reduce(
    (acc, item) => acc + (Number(item.rating) || 5),
    0
  );
  const avg = totalScore / totalReviews;
  const avgFormatted = avg.toFixed(1);
  const roundedStars = Math.max(1, Math.min(5, Math.round(avg)));

  const reviewLabel = totalReviews === 1 ? "1 review" : `${totalReviews} reviews`;

  return (
    <div
      className={`inline-flex items-center gap-3 ${
        variant === "stacked" ? "flex-col sm:flex-row items-start sm:items-center" : ""
      } ${className}`}
      aria-label={`Average rating ${avgFormatted} out of 5 based on ${reviewLabel}`}
    >
      <div className="flex items-center gap-2">
        <span className="font-serif font-normal text-foreground text-sm tracking-wide">
          {avgFormatted}
        </span>
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <img
              key={star}
              src={SPARK_ICON_URL}
              alt=""
              aria-hidden="true"
              className={`size-4 object-contain transition-opacity ${
                star <= roundedStars ? "opacity-100" : "opacity-20 grayscale"
              }`}
            />
          ))}
        </div>
      </div>
      <span className="h-3 w-[1px] bg-border hidden sm:inline-block" aria-hidden="true" />
      <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
        {reviewLabel}
      </span>
    </div>
  );
}
